/**
 * Map Link Generator
 * Implements requirements 2.5, 5.10, 8.2: Google Maps経路URL生成、Apple Maps経路URL生成、座標列からの即時リンク生成
 */

import { Plan, Spot, Walk, Location } from '../types/core';

export interface MapLinkOptions {
    includeWaypoints: boolean;
    travelMode: 'walking' | 'driving' | 'transit';
    language: string;
}

export interface MapLinks {
    google: string;
    apple: string;
    googleDirections: string;
    appleDirections: string;
}

export class MapLinkGenerator {
    private readonly DEFAULT_OPTIONS: MapLinkOptions = {
        includeWaypoints: true,
        travelMode: 'walking',
        language: 'ja'
    };

    /**
     * Generate map links for a complete plan
     * Implements requirements 2.5, 8.2: Google/Apple Maps経路URL生成
     */
    generatePlanMapLinks(
        plan: Plan,
        startLocation: Location,
        options: Partial<MapLinkOptions> = {}
    ): MapLinks {
        const opts = { ...this.DEFAULT_OPTIONS, ...options };

        // Extract coordinates from plan steps
        const coordinates = this.extractCoordinatesFromPlan(plan, startLocation);

        if (coordinates.length === 0) {
            return this.createEmptyMapLinks();
        }

        // Generate different types of links
        const googleDirections = this.generateGoogleDirectionsUrl(coordinates, opts);
        const appleDirections = this.generateAppleDirectionsUrl(coordinates, opts);
        const googleMap = this.generateGoogleMapUrl(coordinates, opts);
        const appleMap = this.generateAppleMapUrl(coordinates, opts);

        return {
            google: googleMap,
            apple: appleMap,
            googleDirections,
            appleDirections
        };
    }

    /**
     * Generate Google Maps directions URL
     * Implements requirement 5.10: 座標列からの即時リンク生成
     */
    private generateGoogleDirectionsUrl(
        coordinates: Location[],
        options: MapLinkOptions
    ): string {
        if (coordinates.length < 2) {
            return '';
        }

        const baseUrl = 'https://www.google.com/maps/dir/';
        const origin = coordinates[0];
        const destination = coordinates[coordinates.length - 1];
        const waypoints = coordinates.slice(1, -1);

        // Build URL parameters
        const params = new URLSearchParams();

        // Set travel mode
        switch (options.travelMode) {
            case 'walking':
                params.set('dirflg', 'w');
                break;
            case 'driving':
                params.set('dirflg', 'd');
                break;
            case 'transit':
                params.set('dirflg', 'r');
                break;
        }

        // Set language
        params.set('hl', options.language);

        // Build coordinate string
        let coordinateString = `${origin.lat},${origin.lng}`;

        if (options.includeWaypoints && waypoints.length > 0) {
            // Add waypoints (Google Maps supports up to 25 waypoints)
            const limitedWaypoints = waypoints.slice(0, 23); // Leave room for destination
            coordinateString += '/' + limitedWaypoints
                .map(coord => `${coord.lat},${coord.lng}`)
                .join('/');
        }

        coordinateString += `/${destination.lat},${destination.lng}`;

        return `${baseUrl}${coordinateString}?${params.toString()}`;
    }

    /**
     * Generate Apple Maps directions URL
     * Implements requirement 5.10: 座標列からの即時リンク生成
     */
    private generateAppleDirectionsUrl(
        coordinates: Location[],
        options: MapLinkOptions
    ): string {
        if (coordinates.length < 2) {
            return '';
        }

        const origin = coordinates[0];
        const destination = coordinates[coordinates.length - 1];

        // Apple Maps URL scheme
        const params = new URLSearchParams();
        params.set('saddr', `${origin.lat},${origin.lng}`);
        params.set('daddr', `${destination.lat},${destination.lng}`);

        // Set travel mode
        switch (options.travelMode) {
            case 'walking':
                params.set('dirflg', 'w');
                break;
            case 'driving':
                params.set('dirflg', 'd');
                break;
            case 'transit':
                params.set('dirflg', 'r');
                break;
        }

        return `https://maps.apple.com/?${params.toString()}`;
    }

    /**
     * Generate Google Maps static map URL
     */
    private generateGoogleMapUrl(
        coordinates: Location[],
        options: MapLinkOptions
    ): string {
        if (coordinates.length === 0) {
            return '';
        }

        const baseUrl = 'https://www.google.com/maps/search/';
        const params = new URLSearchParams();

        // Use first coordinate as search center
        const center = coordinates[0];
        params.set('api', '1');
        params.set('query', `${center.lat},${center.lng}`);

        return `${baseUrl}?${params.toString()}`;
    }

    /**
     * Generate Apple Maps static map URL
     */
    private generateAppleMapUrl(
        coordinates: Location[],
        options: MapLinkOptions
    ): string {
        if (coordinates.length === 0) {
            return '';
        }

        const center = coordinates[0];
        const params = new URLSearchParams();
        params.set('ll', `${center.lat},${center.lng}`);
        params.set('z', '15'); // Zoom level

        return `https://maps.apple.com/?${params.toString()}`;
    }

    /**
     * Extract coordinates from plan steps in order
     */
    private extractCoordinatesFromPlan(plan: Plan, startLocation: Location): Location[] {
        const coordinates: Location[] = [startLocation];

        for (const step of plan.steps) {
            if (step.type === 'spot') {
                const spot = step as Spot;
                coordinates.push(spot.location);
            } else if (step.type === 'walk') {
                const walk = step as Walk;
                // Add destination of walk (which should be the next spot)
                if (!this.isSameLocation(coordinates[coordinates.length - 1], walk.to)) {
                    coordinates.push(walk.to);
                }
            }
        }

        // Add return to start if not already there
        const lastCoord = coordinates[coordinates.length - 1];
        if (!this.isSameLocation(lastCoord, startLocation)) {
            coordinates.push(startLocation);
        }

        return coordinates;
    }

    /**
     * Generate shareable map link for a specific spot
     */
    generateSpotMapLinks(spot: Spot): MapLinks {
        const coordinates = [spot.location];

        const googleUrl = this.generateGooglePlaceUrl(spot);
        const appleUrl = this.generateApplePlaceUrl(spot);

        return {
            google: googleUrl,
            apple: appleUrl,
            googleDirections: googleUrl,
            appleDirections: appleUrl
        };
    }

    /**
     * Generate Google Maps place URL for a specific spot
     */
    private generateGooglePlaceUrl(spot: Spot): string {
        const params = new URLSearchParams();
        params.set('api', '1');

        // Use place name if available, otherwise coordinates
        if (spot.name) {
            params.set('query', `${spot.name} ${spot.location.address || ''}`);
            params.set('query_place_id', spot.sourceId);
        } else {
            params.set('query', `${spot.location.lat},${spot.location.lng}`);
        }

        return `https://www.google.com/maps/search/?${params.toString()}`;
    }

    /**
     * Generate Apple Maps place URL for a specific spot
     */
    private generateApplePlaceUrl(spot: Spot): string {
        const params = new URLSearchParams();
        params.set('ll', `${spot.location.lat},${spot.location.lng}`);

        if (spot.name) {
            params.set('q', spot.name);
        }

        return `https://maps.apple.com/?${params.toString()}`;
    }

    /**
     * Generate route optimization URL for multiple spots
     */
    generateOptimizedRouteLinks(
        spots: Spot[],
        startLocation: Location,
        options: Partial<MapLinkOptions> = {}
    ): MapLinks {
        const opts = { ...this.DEFAULT_OPTIONS, ...options };

        if (spots.length === 0) {
            return this.createEmptyMapLinks();
        }

        // For route optimization, we'll create a simple route through all spots
        const coordinates = [startLocation, ...spots.map(spot => spot.location), startLocation];

        const googleDirections = this.generateGoogleDirectionsUrl(coordinates, opts);
        const appleDirections = this.generateAppleDirectionsUrl(coordinates, opts);

        return {
            google: googleDirections,
            apple: appleDirections,
            googleDirections,
            appleDirections
        };
    }

    /**
     * Generate walking route with estimated times
     */
    generateWalkingRouteWithTimes(
        plan: Plan,
        startLocation: Location
    ): {
        mapLinks: MapLinks;
        routeInfo: {
            totalDistance: number;
            totalWalkingTime: number;
            segments: Array<{
                from: string;
                to: string;
                distance: number;
                duration: number;
                type: 'walk' | 'stay';
            }>;
        };
    } {
        const mapLinks = this.generatePlanMapLinks(plan, startLocation);
        const segments: any[] = [];
        let totalDistance = 0;
        let totalWalkingTime = 0;

        let currentLocation = startLocation;
        let locationName = '出発地点';

        for (const step of plan.steps) {
            if (step.type === 'walk') {
                const walk = step as Walk;
                segments.push({
                    from: locationName,
                    to: '次の目的地',
                    distance: walk.distance,
                    duration: walk.duration,
                    type: 'walk'
                });
                totalDistance += walk.distance;
                totalWalkingTime += walk.duration;
                currentLocation = walk.to;
            } else if (step.type === 'spot') {
                const spot = step as Spot;
                segments.push({
                    from: spot.name,
                    to: spot.name,
                    distance: 0,
                    duration: spot.duration,
                    type: 'stay'
                });
                locationName = spot.name;
                currentLocation = spot.location;
            }
        }

        return {
            mapLinks,
            routeInfo: {
                totalDistance,
                totalWalkingTime,
                segments
            }
        };
    }

    /**
     * Generate QR code data for map links (for easy mobile sharing)
     */
    generateQRCodeData(mapLinks: MapLinks): {
        google: string;
        apple: string;
        universal: string;
    } {
        // Create a universal link that detects device type
        const universalLink = this.createUniversalMapLink(mapLinks);

        return {
            google: mapLinks.googleDirections,
            apple: mapLinks.appleDirections,
            universal: universalLink
        };
    }

    /**
     * Create a universal map link that works on both iOS and Android
     */
    private createUniversalMapLink(mapLinks: MapLinks): string {
        // This would typically be a custom URL that redirects based on user agent
        // For now, return Google Maps as it works on most devices
        return mapLinks.googleDirections;
    }

    /**
     * Validate map link URLs
     */
    validateMapLinks(mapLinks: MapLinks): {
        isValid: boolean;
        errors: string[];
    } {
        const errors: string[] = [];

        if (!mapLinks.google || !this.isValidUrl(mapLinks.google)) {
            errors.push('Invalid Google Maps URL');
        }

        if (!mapLinks.apple || !this.isValidUrl(mapLinks.apple)) {
            errors.push('Invalid Apple Maps URL');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Check if two locations are the same (within 10m)
     */
    private isSameLocation(loc1: Location, loc2: Location): boolean {
        const distance = this.calculateDistance(loc1, loc2);
        return distance < 10;
    }

    /**
     * Calculate distance between two locations (simple approximation)
     */
    private calculateDistance(loc1: Location, loc2: Location): number {
        const R = 6371000; // Earth's radius in meters
        const φ1 = (loc1.lat * Math.PI) / 180;
        const φ2 = (loc2.lat * Math.PI) / 180;
        const Δφ = ((loc2.lat - loc1.lat) * Math.PI) / 180;
        const Δλ = ((loc2.lng - loc1.lng) * Math.PI) / 180;

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    }

    /**
     * Validate URL format
     */
    private isValidUrl(url: string): boolean {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Create empty map links object
     */
    private createEmptyMapLinks(): MapLinks {
        return {
            google: '',
            apple: '',
            googleDirections: '',
            appleDirections: ''
        };
    }

    /**
     * Generate map links with custom parameters for sharing
     */
    generateSharableMapLinks(
        plan: Plan,
        startLocation: Location,
        shareText?: string
    ): {
        mapLinks: MapLinks;
        shareUrls: {
            line: string;
            twitter: string;
            facebook: string;
        };
    } {
        const mapLinks = this.generatePlanMapLinks(plan, startLocation);

        // Create share text
        const defaultShareText = this.generateShareText(plan);
        const finalShareText = shareText || defaultShareText;

        // Generate social sharing URLs
        const shareUrls = {
            line: this.generateLineShareUrl(finalShareText, mapLinks.google),
            twitter: this.generateTwitterShareUrl(finalShareText, mapLinks.google),
            facebook: this.generateFacebookShareUrl(finalShareText, mapLinks.google)
        };

        return {
            mapLinks,
            shareUrls
        };
    }

    /**
     * Generate default share text for a plan
     */
    private generateShareText(plan: Plan): string {
        const spots = plan.steps.filter(step => step.type === 'spot') as Spot[];
        const spotNames = spots.map(spot => spot.name).join('→');

        return `${Math.floor(plan.estimatedDuration)}分のお出かけプラン: ${spotNames}`;
    }

    /**
     * Generate LINE share URL
     */
    private generateLineShareUrl(text: string, mapUrl: string): string {
        const message = `${text}\n${mapUrl}`;
        return `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(mapUrl)}&text=${encodeURIComponent(text)}`;
    }

    /**
     * Generate Twitter share URL
     */
    private generateTwitterShareUrl(text: string, mapUrl: string): string {
        const tweetText = `${text} ${mapUrl}`;
        return `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    }

    /**
     * Generate Facebook share URL
     */
    private generateFacebookShareUrl(text: string, mapUrl: string): string {
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(mapUrl)}&quote=${encodeURIComponent(text)}`;
    }
}